import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

/**
 * State that lives in the URL.
 *
 * A filtered table is a place, not a mood: refreshing should show what you were
 * looking at, and a link you send someone should show them the same thing. So the
 * search box, every filter and the active tab go in the query string.
 *
 * Defaults are omitted from the URL rather than written out, so an unfiltered page
 * is a clean address and only what you actually changed is visible in it.
 *
 * **The value leads and the URL follows, which is not a preference.** React Router
 * routes a navigation through a transition, so `useSearchParams` does not re-render
 * inside the event that changed it — and React then restores the input to the value
 * it last rendered, discarding the keystroke. Typing "ops" quickly landed on "os",
 * every time the machine was busy enough. Holding the value in ordinary state
 * re-renders inside the event, so what was typed is what stays; the URL catches up
 * once typing pauses, which also stops one history write per keystroke.
 */
const SETTLE_MS = 250;

function useMirroredParams(spec) {
    const [params, setParams] = useSearchParams();

    // Callers pass an object literal, so `spec` is a new value every render. Held
    // in a ref, the setters keep a stable identity — otherwise any effect that
    // depended on one would re-run forever.
    const latest = useRef(spec);
    latest.current = spec;

    const readUrl = useCallback(
        (source) => Object.fromEntries(Object.entries(latest.current).map(([key, fallback]) => [key, source.get(key) ?? fallback])),
        []
    );

    const [values, setValues] = useState(() => readUrl(params));
    const timer = useRef(null);
    const queued = useRef(new Map());

    /*
     * The URL wins whenever it changes with nothing of ours in flight: a back
     * button, a link followed, a fresh load. While a write is pending it does not,
     * or the half-applied parameters would overwrite what is still being typed.
     */
    useEffect(() => {
        if (timer.current) return;
        setValues((current) => {
            const next = readUrl(params);
            return Object.keys(next).every((key) => next[key] === current[key]) ? current : next;
        });
    }, [params, readUrl]);

    // A pending write is abandoned on unmount; you navigated away from it.
    useEffect(() => () => clearTimeout(timer.current), []);

    const flush = useCallback(() => {
        const writes = new Map(queued.current);
        queued.current.clear();
        timer.current = null;

        setParams(
            (current) => {
                const updated = new URLSearchParams(current);
                for (const [key, value] of writes) {
                    if (!value || value === latest.current[key]) updated.delete(key);
                    else updated.set(key, value);
                }
                return updated;
            },
            // Filtering is not navigation: it should not fill the back button
            // with one entry per keystroke.
            { replace: true }
        );
    }, [setParams]);

    const set = useCallback(
        (key, next) => {
            // Empty reads back as the default, so that is what it becomes here too.
            setValues((current) => ({ ...current, [key]: next || latest.current[key] }));

            // Every key that moved, not merely the last one: two filters changed
            // inside the same pause are two parameters to write.
            queued.current.set(key, next);
            clearTimeout(timer.current);
            timer.current = setTimeout(flush, SETTLE_MS);
        },
        [flush]
    );

    const clear = useCallback(() => {
        clearTimeout(timer.current);
        timer.current = null;
        queued.current.clear();

        setValues(Object.fromEntries(Object.entries(latest.current)));
        setParams(
            (current) => {
                const updated = new URLSearchParams(current);
                for (const key of Object.keys(latest.current)) updated.delete(key);
                return updated;
            },
            { replace: true }
        );
    }, [setParams]);

    return [values, set, clear];
}

export function useUrlState(key, fallback = "") {
    const [values, set] = useMirroredParams({ [key]: fallback });
    const setValue = useCallback((next) => set(key, next), [set, key]);
    return [values[key], setValue];
}

/** Several keys at once, so a page does not call the hook five times. */
export function useUrlStates(spec) {
    return useMirroredParams(spec);
}
